/* VSS Our Brands v2.0.0. No third-party dependencies or product mutations. */
(() => {
  if (customElements.get('vss-brand-directory')) return;
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const firstLetter = value => {
    const letter = normalize(value).charAt(0).toUpperCase();
    return /^[A-Z]$/.test(letter) ? letter : '#';
  };
  const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });

  class VSSBrandDirectory extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.frame = requestAnimationFrame(() => this.start());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.abort?.abort();
      this.initialized = false;
    }

    async start() {
      if (!this.isConnected || this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.grid = this.querySelector('[data-brand-grid]');
      this.controls = this.querySelector('[data-controls]');
      this.status = this.querySelector('[data-result-count]');
      if (!this.grid || !this.controls || !this.status) return;
      this.category = 'all';
      this.letter = 'all';
      this.pagination = this.querySelector('[data-pagination]');
      this.loadedPages = new Set([Number(this.pagination?.dataset.page || 1)]);
      this.querySelector('[data-results-bar]').hidden = false;
      this.controls.hidden = true;
      this.querySelector('[data-empty]').hidden = true;
      this.bind();
      await this.loadAllBrands();
    }

    bind() {
      const options = { signal: this.abort.signal };
      this.controls.addEventListener('click', event => {
        const categoryButton = event.target.closest('button[data-category]');
        const letterButton = event.target.closest('button[data-letter]');
        if (categoryButton && !categoryButton.disabled) {
          this.category = categoryButton.dataset.category;
          // A specialty change starts from all letters.
          this.letter = 'all';
          this.applyFilters();
        } else if (letterButton && !letterButton.disabled) {
          this.letter = letterButton.dataset.letter;
          this.applyFilters();
        }
      }, options);
      this.querySelector('[data-reset]').addEventListener('click', () => {
        this.resetFilters();
        this.querySelector('[data-category="all"]')?.focus();
      }, options);
      this.querySelector('[data-retry]').addEventListener('click', () => this.loadAllBrands(), options);
      document.addEventListener('shopify:block:select', event => {
        if (!this.cards) return;
        const selected = this.cards.find(card => card.element.dataset.blockId === event.detail?.blockId);
        if (selected) {
          this.resetFilters();
          selected.element.scrollIntoView({ block: 'center' });
        }
      }, options);
    }

    async loadAllBrands() {
      if (this.loading || this.abort.signal.aborted) return;
      this.loading = true;
      this.querySelector('[data-load-error]').hidden = true;
      this.status.textContent = this.dataset.loadingLabel;
      this.grid.setAttribute('aria-busy', 'true');
      try {
        const pages = Number(this.pagination?.dataset.pages || 1);
        const param = this.pagination?.dataset.pageParam;
        for (let page = 1; page <= pages; page += 1) {
          if (this.loadedPages.has(page)) continue;
          if (!param) throw new Error('Pagination parameter is missing.');
          const url = new URL(window.location.href);
          url.hash = '';
          url.searchParams.delete('sections');
          url.searchParams.set('section_id', this.dataset.sectionId);
          url.searchParams.set(param, String(page));
          const response = await fetch(url.toString(), { credentials: 'same-origin', signal: this.abort.signal });
          if (!response.ok) throw new Error('Brand page could not be loaded.');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const incomingRoot = html.getElementById(this.id);
          const incomingGrid = incomingRoot?.querySelector('[data-brand-grid]');
          const incomingPage = incomingRoot?.querySelector('[data-pagination]');
          if (!incomingGrid || Number(incomingPage?.dataset.page) !== page) throw new Error('Unexpected brand page.');
          const known = new Set(Array.from(this.grid.querySelectorAll('[data-brand]'), card => card.dataset.key));
          incomingGrid.querySelectorAll('[data-brand]').forEach(card => {
            if (!known.has(card.dataset.key)) {
              this.grid.append(document.importNode(card, true));
              known.add(card.dataset.key);
            }
          });
          this.loadedPages.add(page);
        }
        if (this.abort.signal.aborted) return;
        this.cards = Array.from(this.grid.querySelectorAll('[data-brand]')).map(element => ({
          element,
          name: element.dataset.name,
          letter: firstLetter(element.dataset.name),
          categories: (element.dataset.categories || '').split(',').map(normalize).filter(Boolean)
        })).sort((a, b) => collator.compare(a.name, b.name));
        this.cards.forEach(card => this.grid.append(card.element));
        this.querySelectorAll('[data-category]').forEach(button => {
          button.disabled = button.dataset.category !== 'all' && !this.cards.some(card => card.categories.includes(button.dataset.category));
        });
        if (this.pagination) this.pagination.hidden = true;
        this.controls.hidden = this.cards.length === 0;
        this.applyFilters();
      } catch (error) {
        if (error.name === 'AbortError') return;
        // Keep server-rendered links and pagination available. Never show a
        // partial directory as if filters covered every brand.
        this.status.textContent = this.dataset.errorLabel;
        this.querySelector('[data-load-error]').hidden = false;
        if (this.pagination) this.pagination.hidden = false;
      } finally {
        this.grid.removeAttribute('aria-busy');
        this.loading = false;
      }
    }

    resetFilters() {
      this.category = 'all';
      this.letter = 'all';
      this.applyFilters();
    }

    applyFilters() {
      if (!this.cards) return;
      let visible = 0;
      const baseMatches = this.cards.filter(card => (this.category === 'all' || card.categories.includes(this.category)));
      const possibleLetters = new Set(baseMatches.map(card => card.letter));
      this.cards.forEach(card => {
        const matches = (this.category === 'all' || card.categories.includes(this.category)) && (this.letter === 'all' || card.letter === this.letter);
        card.element.hidden = !matches;
        if (matches) visible += 1;
      });
      this.querySelectorAll('[data-category]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.category === this.category)));
      this.querySelectorAll('[data-letter]').forEach(button => {
        const letter = button.dataset.letter;
        button.setAttribute('aria-pressed', String(letter === this.letter));
        button.disabled = letter !== 'all' && !possibleLetters.has(letter);
        button.hidden = letter === '#' && !this.cards.some(card => card.letter === '#');
      });
      this.status.textContent = (this.dataset.countLabel || '{count} of {total} brands').replace('{count}', visible).replace('{total}', this.cards.length);
      this.querySelector('[data-empty]').hidden = visible !== 0;
      this.querySelector('[data-reset]').hidden = this.category === 'all' && this.letter === 'all';
    }
  }
  customElements.define('vss-brand-directory', VSSBrandDirectory);
})();
